import { Badge } from '@mantine/core';
import type { FieldState } from '../../types/fieldState';
import type { FieldEvent } from '../../types/fieldEvent';
import { FieldEventTypes, FieldRecordTypes } from '../../types/fieldEvent';
import { formatDateMD } from '../../utils/dateUtils';
import { colors } from '../../theme/colors';

const PEST_CONTROL_BADGE_STYLE = {
  backgroundColor: colors.workType.pestControl.bg,
  color: '#000',
} as const;

interface FieldStatusBadgeProps {
  /** 圃場ステート */
  state: FieldState;
  /** 圃場イベント一覧 */
  events: FieldEvent[];
}

/**
 * 圃場ステータスバッジ
 *
 * 圃場ステートと圃場イベント（SCHEDULED含む）に応じてバッジを表示
 * - BEFORE_SEEDING（予定日なし）: gray「播種日未定」
 * - BEFORE_SEEDING（予定日あり）: yellow「M/D 播種予定」
 * - BEFORE_EMERGENCE（出芽予定あり）: green「M/D 出芽見込み」
 * - BEFORE_EMERGENCE（出芽予定なし）: green「出芽待ち」
 * - INITIAL_GROWTH: green「初期生育期」
 * - PEST_CONTROL（予定あり）: teal「M/D 防除N予定」（カレンダー帯と同じ workType.pestControl.bg=#c3fae8）
 * - PEST_CONTROL（予定なし）: teal「防除期」
 * - HARVESTABLE（実施日あり）: cyan(filled)「M/D 収穫済み」
 * - HARVESTABLE（予定日あり）: cyan-2 塗り + 黒文字「M/D 収穫予定」（Figma ProcessChip）
 * - HARVESTABLE（どちらもなし）: cyan-1 塗り + disabled(gray-5) 文字「収穫日未定」（Figma ProcessChip）
 *
 */
export function FieldStatusBadge({ state, events }: FieldStatusBadgeProps) {
  switch (state) {
    case 'BEFORE_SEEDING': {
      const seedingScheduled = events.find(
        (e) =>
          e.eventType === FieldEventTypes.SEEDING && e.recordType === FieldRecordTypes.SCHEDULED
      );
      if (!seedingScheduled) {
        return (
          <Badge color="gray" variant="light" size="md">
            播種日未定
          </Badge>
        );
      }
      return (
        <Badge color="yellow" variant="light" size="md" style={{ color: '#000' }}>
          {formatDateMD(seedingScheduled.eventDate)} 播種予定
        </Badge>
      );
    }

    case 'BEFORE_EMERGENCE': {
      const emergenceScheduled = events.find(
        (e) =>
          e.eventType === FieldEventTypes.EMERGENCE && e.recordType === FieldRecordTypes.SCHEDULED
      );
      if (emergenceScheduled) {
        return (
          <Badge color="green" variant="light" size="md" style={{ color: '#000' }}>
            {formatDateMD(emergenceScheduled.eventDate)} 出芽見込み
          </Badge>
        );
      }
      return (
        <Badge color="green" variant="light" size="md" style={{ color: '#000' }}>
          出芽待ち
        </Badge>
      );
    }

    case 'INITIAL_GROWTH':
      return (
        <Badge color="green" variant="light" size="md" style={{ color: '#000' }}>
          初期生育期
        </Badge>
      );

    case 'PEST_CONTROL': {
      // 次の防除予定を探す（SCHEDULED があり ACTUAL がまだないもの）
      const pestControlScheduled = events
        .filter(
          (e) =>
            e.eventType === FieldEventTypes.PEST_CONTROL &&
            e.recordType === FieldRecordTypes.SCHEDULED
        )
        .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
      const actualSeqNumbers = new Set(
        events
          .filter(
            (e) =>
              e.eventType === FieldEventTypes.PEST_CONTROL &&
              e.recordType === FieldRecordTypes.ACTUAL
          )
          .map((e) => e.sequenceNumber)
      );
      const nextScheduled = pestControlScheduled.find(
        (e) => !actualSeqNumbers.has(e.sequenceNumber)
      );

      if (nextScheduled) {
        return (
          <Badge variant="light" size="md" style={PEST_CONTROL_BADGE_STYLE}>
            {formatDateMD(nextScheduled.eventDate)} 防除{nextScheduled.sequenceNumber}予定
          </Badge>
        );
      }
      return (
        <Badge variant="light" size="md" style={PEST_CONTROL_BADGE_STYLE}>
          防除期
        </Badge>
      );
    }

    case 'HARVESTABLE': {
      // 収穫実施日（HARVEST/ACTUAL）があれば「M/D 収穫済み」、なければ収穫予定日
      // （HARVEST/SCHEDULED）で「M/D 収穫予定」、どちらもなければ「収穫日未定」。
      // HARVEST は単発イベントで sequenceNumber は常に 1 のため連番ソートは不要。
      const harvestActual = events.find(
        (e) => e.eventType === FieldEventTypes.HARVEST && e.recordType === FieldRecordTypes.ACTUAL
      );
      if (harvestActual) {
        return (
          <Badge color="cyan" variant="filled" size="md">
            {formatDateMD(harvestActual.eventDate)} 収穫済み
          </Badge>
        );
      }
      const harvestScheduled = events.find(
        (e) =>
          e.eventType === FieldEventTypes.HARVEST && e.recordType === FieldRecordTypes.SCHEDULED
      );
      if (harvestScheduled) {
        return (
          <Badge
            variant="light"
            size="md"
            style={{ backgroundColor: 'var(--mantine-color-cyan-2)', color: '#000' }}
          >
            {formatDateMD(harvestScheduled.eventDate)} 収穫予定
          </Badge>
        );
      }
      return (
        <Badge
          variant="light"
          size="md"
          style={{
            backgroundColor: 'var(--mantine-color-cyan-1)',
            color: 'var(--mantine-color-gray-5)',
          }}
        >
          収穫日未定
        </Badge>
      );
    }
  }
}
